import { ChartCard } from "@/components/chart-card";
import { ApiPath } from "@/api/path";
import { useDateRange } from "@/dateRanges/DateRangeContext";
import { useSelectedOrganizationId } from "@/organization/api";
import { InstallationData } from "@/installation/installation";
import { useAggregateNormalizeContext } from "@/installation/AggregateNormalizeContext";
import { ErrorBoundary } from "react-error-boundary";
import {
  electricityConsumptionUnits,
  useElectricityConsumptionUnitContext,
} from "@/energyPrices/ElectricityConsumptionUnitContext";
import { OFF } from "@/dateRanges/refreshIntervals";
import { LineChart } from "lucide-react";
import { PhaseTypeConfigurationTable } from "@/refrigerant/PhaseTypeConfigurationTable";
import { useQueries } from "@tanstack/react-query";
import { lineCharts } from "@/components/charts/api";
import { LineChartResponse } from "@/components/charts/lineChartResponse";
import { differenceInHours } from "date-fns";

const AssetsEnergyConsumptionChartCard = ({ assets }: { assets: InstallationData[] }) => {

  const { windowSize, dateRange } = useDateRange()
  const organizationId = useSelectedOrganizationId();
  const { queryWithAggregateNormalize, unitWithAggregateNormalize } = useAggregateNormalizeContext();

  const { electricityConsumptionUnit } = useElectricityConsumptionUnitContext();

  // Check if any asset is missing phase_system_type
  const assetsWithoutPhaseType = assets.filter(asset => !asset.phase_system_type);

  if (assetsWithoutPhaseType.length > 0) {
    return (
      <div className="flex h-80 w-full flex-1 flex-col gap-4 items-center justify-center rounded text-thalo-foreground border border-thalo-border bg-card p-4">
        <LineChart size={48} className="text-muted-foreground"/>
        <p className="text-center">Energy consumption data is not available.</p>
        <p className="text-center text-sm text-muted-foreground">Phase system type configuration is required.</p>
        <PhaseTypeConfigurationTable assets={assetsWithoutPhaseType} />
      </div>
    );
  }

  // Fetch data to check data point count
  const queryParams = queryWithAggregateNormalize({
    windowSize: windowSize,
    dateRange: dateRange,
    organization_id: organizationId,
    installation_id: assets.map(asset => asset.id),
    target_unit: electricityConsumptionUnit
  });

  const dataQueries = useQueries({
    queries: [
      {
        ...lineCharts.detail(
          ApiPath.INSTALLATION_REFRIGERANT_AGGREGATED_ENERGY,
          queryParams
        ),
        select: (data: LineChartResponse) => {
          // Count total data points across all series
          let totalPoints = 0;
          if (data && data.series && Array.isArray(data.series)) {
            data.series.forEach((s) => {
              if (s.data && Array.isArray(s.data)) {
                totalPoints += s.data.length;
              }
            });
          }
          return totalPoints;
        }
      }
    ]
  });

  const dataPointCount = dataQueries[0]?.data;
  const isLoading = dataQueries[0]?.isLoading;
  
  // Check if the date range is recent (ends within last 48 hours)
  const dateRangeEnd = dateRange.value.to();
  const now = new Date();
  const hoursSinceRangeEnd = differenceInHours(now, dateRangeEnd);
  const isRecentDateRange = hoursSinceRangeEnd >= 0 && hoursSinceRangeEnd <= 48;
  
  // Only show message if: date range is recent AND we have minimal data (1-2 points)
  const hasMinimalData = typeof dataPointCount === 'number' && dataPointCount > 0 && dataPointCount <= 2;
  const shouldShowMessage = !isLoading && hasMinimalData && isRecentDateRange;

  // Show minimal data message if we have 1-2 data points in a recent date range
  if (shouldShowMessage) {
    return (
      <div className="flex h-80 w-full flex-1 flex-col gap-4 items-center justify-center rounded text-thalo-foreground border border-thalo-border bg-card p-4">
        <LineChart size={48} className="text-muted-foreground"/>
        <p className="text-center font-medium">Equipment recently turned on</p>
        <p className="text-center text-sm text-muted-foreground max-w-md">
          This data will show once the equipment has been in use for a full day. 
          Energy consumption charts require at least one day of operational data to display meaningful trends.
        </p>
      </div>
    );
  }

  return (
    <ChartCard
      // title={
      //   <div className="flex items-center">
      //     <UnitSwitcher className="pl-0 font-normal"/>
      //   </div>
      // }
      apiPath={ApiPath.INSTALLATION_REFRIGERANT_AGGREGATED_ENERGY}
      showZoom={false}
      yAxisName={electricityConsumptionUnits[electricityConsumptionUnit]}
      unit={unitWithAggregateNormalize(electricityConsumptionUnits[electricityConsumptionUnit])}
      query={[
        queryParams,
      ]}
      hideIfNoData={false}
      refreshInterval={OFF}
    />
  )
}

export default AssetsEnergyConsumptionChartCard;
